// src/components/features/chat/ToolStatusCard.jsx
// One tool status pill: the step the assistant is running right now, or a
// finished step worth keeping on screen (see chatToolLabels.js for which).
// UPDATED: gold accent colors matching Previu brand

import React from 'react';
import { resolveToolStatus, GENERIC_ERROR_LABEL } from '../../../constants/chatToolLabels';

// A step that finishes quickly never needed a wait indicator, and a pill that
// appears and disappears inside half a second reads as a glitch. The pill
// starts invisible and fades in after this long, so anything faster than it is
// removed before it ever paints. CSS rather than a timer: no state to cancel,
// and a pill that settles into its done state keeps whatever visibility it
// already had.
const APPEAR_AFTER_MS = 500;

export default function ToolStatusCard({ toolName, action, status }) {
  const meta    = resolveToolStatus(toolName, action);
  const running = status === 'running';
  const failed  = status === 'error';

  // Silent tools (e.g. questionnaire answers) never show a pill unless the
  // step failed — a failed step must not vanish silently.
  if (meta.silent && !failed) return null;

  // A pill with no status is a finished step, not a running one. It used to
  // default to running, so any step whose completion was never signalled sat
  // there animating under the answer it had already produced.
  const text = running
    ? meta.label
    : failed
      ? (meta.errorLabel ?? GENERIC_ERROR_LABEL)
      : (meta.doneLabel ?? meta.label);

  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 7,
      padding: '5px 12px',
      borderRadius: 20,
      // Gold tint — matches brand-accent light usage in the app
      background: running ? 'var(--brand-light)' : failed ? 'var(--red-bg)' : 'var(--green-bg)',
      border: `1px solid ${running ? 'var(--amber)' : failed ? 'var(--red)' : 'var(--green)'}`,
      fontSize: 12, fontWeight: 500,
      color: running ? 'var(--amber-text)' : failed ? 'var(--red)' : 'var(--green)',
      width: 'fit-content',
      animation: `pill-appear 0.15s ease ${APPEAR_AFTER_MS}ms both`,
    }}>
      <span style={{ fontSize: 13 }}>{meta.emoji}</span>
      <span>{text}</span>
      {running && (
        <span style={{ display: 'flex', gap: 3, alignItems: 'center', marginLeft: 2 }}>
          {[0, 0.2, 0.4].map((delay, i) => (
            <span key={i} style={{
              width: 4, height: 4, borderRadius: '50%',
              background: 'var(--brand-accent)',   // brand-accent gold
              animation: `dot-bounce 1s ease-in-out ${delay}s infinite`,
            }} />
          ))}
        </span>
      )}
      {!running && !failed && <span style={{ color: 'var(--green)' }}>✓</span>}
      {failed && <span style={{ color: 'var(--red)' }}>✗</span>}

      <style>{`
        @keyframes dot-bounce {
          0%,80%,100% { transform: translateY(0); }
          40%          { transform: translateY(-4px); }
        }
        @keyframes pill-appear {
          from { opacity: 0; transform: translateY(2px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
